import isEmpty from "lodash/isEmpty";
import isObject from "lodash/isObject";
import toString from "lodash/toString";
import size from "lodash/size";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";
import toUpper from "lodash/toUpper";
import replace from "lodash/replace";
import orderBy from "lodash/orderBy";
import get from "lodash/get";
import isNull from "lodash/isNull";
import mapValues from "lodash/mapValues";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import compact from "lodash/compact";
import { application, ApplicationRoutes, sortingOptions } from "./components/constants";
import { NUMBER_OF_VACANCIES_PER_PAGE } from "./configs";

export const generateUniqueKey = (length) => Array(length).fill(null).map(() => Math.floor(Math.random() * 62))
    .map((char) => String.fromCharCode(char + (char > 35 ? 61 : 55)))
    .join('');


export const sortVacancyCards = (positions, option) => {
    const sortOpenPositionsOptions = {
        [sortingOptions.Date]: () => [...positions].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    };
    return (sortOpenPositionsOptions[option] || sortOpenPositionsOptions[sortingOptions.Date])();
};

export const setIntoSessionStorage = (key, value) => {
    try {
        sessionStorage.setItem(key, value);
    } catch (error) {
        console.error("Error setting sessionStorage item", error);
    }
};

export const getFromSessionStorage = (key) => {
    try {
        return JSON.parse(sessionStorage.getItem(key));
    } catch (error) {
        console.error("Error getting sessionStorage item", error);
        return null;
    }
};

export const extendedGetFromSessionStorage = (key) => {
    try {
        const item = sessionStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    } catch (error) {
        console.error("Error getting/parsing sessionStorage item", error);
        return null;  
    }
};

export const extendedSetIntoSessionStorage = (key, value) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error("Error setting sessionStorage item", error);
    }
};

export const sanitizeString = (string) => {
    if (typeof string !== 'string') {
        return '';
    }
    return string.replace(/^\s*[\r\n]+|[\s\n]+/g, ' ').trim();
};

export const checkIfUserIsFromWlh = (configuration) => configuration?.email?.match(/@([^.@]+)\.\w+/)?.[1] === "welovehumans";

export const checkIfUserIsDirector = (configuration) => configuration?.role === "director";

export const userRules = {
    isAdmin: (configuration) => configuration && !!configuration.is_admin
}

export const customerRules = {
    isRecruiter: (customer) => customer && customer.type === "recruiter"
}

export const formatNumber = (number) => toString(number).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const isValidEmail = (email) => {
    // Regular expression for validating an email
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
}

export const isValidIBAN = (iban = "") => {
    const cleaned = toUpper(replace(iban, /\s+/g, ""));
    const regex = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
    const isFormatValid = regex.test(cleaned);
    const isLengthValid = size(cleaned) >= 15 && size(cleaned) <= 34;
    return isFormatValid && isLengthValid;
};

export const isObjectEmpty = (obj) => { 
    return isEmpty(obj) || !isObject(obj); 
};

export const getNumberOfPagesForVacancies = (numberOfVacancies) => {
    return Math.ceil(numberOfVacancies / NUMBER_OF_VACANCIES_PER_PAGE);
};

export const getTokenFromStorage = () => window.localStorage.getItem("token");

export const isNonEmptyString = (value) => {
    return value.trim() !== "" || value === "";
};

export const isNonEmptyItem = (value) => {
    return value.trim() !== "";
};

export const randomIndex = Math.floor(Math.random() * 3);

export function convertToInteger(value) {
    if (typeof value === 'number') return value;
    if (!value) return null;
    const numberWithoutDot = value.replace('.', '');
    return parseInt(numberWithoutDot, 10);
}

export const areArraysEqual = (arr1, arr2) => {
    if (!arr1 || !arr2) return arr1 === arr2;
    if (size(arr1) !== size(arr2)) return false;
    return isEqual(sortBy(arr1), sortBy(arr2));
};

export const generateSessionToken = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = crypto.getRandomValues(new Uint8Array(1))[0] & 15 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export const calculateTimeAgo = (dateString, locale = "en") => {
    let utcString = dateString;
    const hasTimezoneOffset = /[+-]\d{2}:\d{2}$/.test(dateString);
    const hasZ = dateString.endsWith('Z');
    if (!hasZ && !hasTimezoneOffset) {
      utcString += 'Z';
    }

    const createdDate = new Date(utcString);
    const now = new Date();
    const diffInMs = now - createdDate;
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    const justNow = locale === "en" ? "Just now" : "Zojuist"
    const oneDayAgo = locale === "en" ? "1 day ago" : "dag geleden"
    const hoursAgo = locale === "en" ? `${diffInHours} ${diffInHours > 1 ? "hours" : "hour"} ago` : `${diffInHours} uur geleden`
    const minutesAgo = locale === "en" ? `${diffInMinutes} ${diffInMinutes > 1 ? "minutes" : "minute"} ago` : `${diffInMinutes} ${diffInMinutes > 1 ? "minuten" : "minuut"} geleden`

    if (diffInMs < 0) {
      return justNow;
    }

    if (diffInDays > 1) {
      return createdDate.toLocaleDateString("default", {
        month: "short",
        day: "numeric",
      });
    }

    if (diffInDays === 1) {
      return oneDayAgo;
    }

    if (diffInHours >= 1) {
      return hoursAgo;
    }

    if (diffInMinutes >= 1) {
      return minutesAgo;
    }
    
    return justNow;
  };

export const convertDateToISO = (dateStr) => {
  if (dateStr) {
      const [year, month, day] = dateStr.split("/");
      const date = new Date(Date.UTC(year, Number(month) - 1, Number(day)));
      return date.toISOString();
  }
}

export const isValidUrl = (value) => {
  // Regular expression for validating a url
  const urlPattern = new RegExp('^(https?:\\/\\/)?'+ 
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ 
      '((\\d{1,3}\\.){3}\\d{1,3}))'+ 
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ 
      '(\\?[;&a-z\\d%_.~+=-]*)?'+ 
      '(\\#[-a-z\\d_]*)?$','i'); 
  return urlPattern.test(value);
};

export const formatWebsiteUrl = (url) => {
    if (!url) return "";
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

export const getCurrencySymbol = (value) => value === "USD" ? "$" : "€";

export const extractMonthAndYearFromDate = (dateString) => {
    const date = new Date(dateString);
    const monthName = date.toLocaleString('en-US', { month: "short" });
    const year = date.getFullYear();
    return `${monthName} ${year}`;
  }

export const generateYearRange = (startOffset, endOffset, minYear = null) => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - startOffset;
    const computedEndYear = currentYear + endOffset;
    const endYear = computedEndYear > currentYear ? currentYear : computedEndYear;
    let years = Array.from(
        { length: endYear - startYear + 1 },
        (_, i) => startYear + i
    );
    if (minYear !== null) {
        years = years.filter((year) => year >= minYear);
        years = orderBy(years, [(y) => y], ['asc']);
    } else {
        years = orderBy(years, [(y) => y], ['desc']);
    }
    return years;
};

export const authActions = {
  resetLocalStorageAuthData: () => {
    window.localStorage.removeItem("token");
    window.localStorage.removeItem("is-onboarded");
    window.localStorage.removeItem("application");
  },

  setLocalStorageAuthData: ({ data }) => {
    localStorage.setItem("token", get(data, "access_token"))
    localStorage.setItem("is-onboarded", get(data, "is_onboarded"))
    localStorage.setItem("application", application.hi5)
  }
}

export const classifyActiveSessionRoute = () => {
  const rootApplication = window.localStorage.getItem("application");

  if (isNull(rootApplication)) {
    return "/sign-in";
  }

  return rootApplication === application.hi5 ? ApplicationRoutes.hi5Root : "/sign-in";
};

export const replaceEmptyStringsWithNull = (obj) => {
  return mapValues(obj, value => value === "" ? null : value);
}

export const checkPhoneNumberSupportage = ({ phoneNumber, rules }) => {
  if (isNil(phoneNumber)) return;
  const isCountryCodeSupported = Object.values(rules).some(code => phoneNumber.startsWith(code));
  return isCountryCodeSupported;
}

export function calculateHi5ExperiencePeriod({ dateFrom, dateTo }) {
    if (!isString(dateFrom) || isEmpty(dateFrom)) return 'Invalid date';
    const from = new Date(dateFrom);
    const to = dateTo ? new Date(dateTo) : new Date();
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return 'Invalid date';
    const totalMonths = Math.max(
        (to.getFullYear() - from.getFullYear()) * 12 +
        (to.getMonth() - from.getMonth()),
        1
    );
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    const result = compact([
        years && `${years} year${years > 1 ? 's' : ''}`,
        months && `${months} month${months > 1 ? 's' : ''}`
    ]).join(' ');
    return result;
}

export const getBrandColorWithAlpha = (brandColor, n_alpha) => {
  const n_red = parseInt(brandColor.slice(1, 3), 16);
  const n_green = parseInt(brandColor.slice(3, 5), 16);
  const n_blue = parseInt(brandColor.slice(5, 7), 16);

  return `rgba(${n_red}, ${n_green}, ${n_blue}, ${n_alpha})`;
};
